import { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Person } from '@mui/icons-material';
import {
  resolveAttendeePreference,
  type Resolution,
} from '../../lib/group-preference';
import {
  setAttendeeGroupPreferenceFulfilled,
  useActor,
} from '../../lib/audited-writes';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { useUserContext } from '../../lib/users-context';
import { formatGroupShort } from '../../lib/groups';
import type { Attendee, Group } from '../../lib/types';

// Glyph + label for each fragment status. Kept inside the file because
// this is the only place that renders the tooltip — no reason to leak
// the formatting choice into the matcher module.
const GLYPH: Record<Resolution['status'], string> = {
  matched: '✓',
  'cross-grade': '⚠',
  'not-found': '?',
  ambiguous: '?',
  'incomplete-name': '?',
};

// Builds the "destination" suffix for a matched / cross-grade fragment:
// the matched attendee's group's short name, or the literal 'unassigned'
// if they're in the same grade pool but not yet placed in a group.
const groupSuffix = (
  matched: Attendee | undefined,
  groupsByRef: Map<string, Group>,
): string => {
  if (!matched) return '';
  const grp = matched.groupRef ? groupsByRef.get(matched.groupRef) : undefined;
  return grp ? formatGroupShort(grp) : 'unassigned';
};

const fragmentLine = (
  r: Resolution,
  groupsByRef: Map<string, Group>,
): string => {
  const glyph = GLYPH[r.status];
  switch (r.status) {
    case 'matched': {
      const dest = groupSuffix(r.matchedAttendee, groupsByRef);
      const who = r.matchedAttendee
        ? `${r.matchedAttendee.first_name} ${r.matchedAttendee.last_name}`
        : r.fragment;
      return `  ${glyph} ${r.fragment} → ${who} (${dest})`;
    }
    case 'cross-grade': {
      const grade = r.matchedAttendee?.grade_completed ?? '';
      return `  ${glyph} ${r.fragment} → found in Grade ${grade} (different grade — cannot be paired)`;
    }
    case 'ambiguous': {
      const names = (r.ambiguousCandidates ?? [])
        .map((a) => `${a.first_name} ${a.last_name}`)
        .join(', ');
      return `  ${glyph} ${r.fragment} → ambiguous (${names})`;
    }
    case 'incomplete-name':
      return `  ${glyph} ${r.fragment} → first name only (need first + last)`;
    case 'not-found':
    default:
      return `  ${glyph} ${r.fragment} → not found`;
  }
};

// Roster cell: a colored "person" icon whose tooltip explains the
// resolved-vs-unresolved state of the attendee's preference and whose
// click (admin-only) cycles the manual fulfillment flag.
//   undefined  → blue (info)             — pending review
//   'manual'   → green (success)         — admin asserted "looks good"
//   'auto'     → purple (secondary)      — auto-assign placed the cluster
//   'rejected' → red (error)             — admin reviewed; can't fulfill
//
// Admin click cycle: undefined → 'manual' → 'rejected' → undefined.
// 'auto' is a one-time promotion path: clicking 'auto' moves to
// 'manual' (admin endorses the auto placement). Cycling back through
// 'auto' is intentionally not supported — auto is only written by
// autoAssignAttendees.
const GroupPreferenceIcon = ({ attendee }: { attendee: Attendee }) => {
  const { attendees, groups } = useFirebaseContext();
  const { isAdmin } = useUserContext();
  const actor = useActor();
  const toast = useToast();

  const resolution = useMemo(
    () => resolveAttendeePreference(attendee, attendees),
    [attendee, attendees],
  );

  const groupsByRef = useMemo(() => {
    const m = new Map<string, Group>();
    groups.forEach((g) => m.set(g.ref, g));
    return m;
  }, [groups]);

  if (!attendee.group_preference) return null;

  const fulfilled = attendee.group_preference_fulfilled;
  const color: 'info' | 'success' | 'secondary' | 'error' =
    fulfilled === 'manual'
      ? 'success'
      : fulfilled === 'auto'
        ? 'secondary'
        : fulfilled === 'rejected'
          ? 'error'
          : 'info';

  const statusLine =
    fulfilled === 'manual'
      ? 'Fulfilled (marked by admin)'
      : fulfilled === 'auto'
        ? 'Auto-fulfilled (placed together by auto-assign)'
        : fulfilled === 'rejected'
          ? 'Rejected (reviewed by admin — cannot fulfill)'
          : 'Pending review';

  const fragmentSection =
    resolution.fragments.length > 0
      ? `\n\nMatched:\n${resolution.fragments
          .map((r) => fragmentLine(r, groupsByRef))
          .join('\n')}`
      : '';

  const noteSection = attendee.group_preference_note
    ? `\n\nNote: ${attendee.group_preference_note}`
    : '';

  // Click cycle: undefined → manual → rejected → undefined. 'auto'
  // promotes to 'manual' (admin endorses). Hint mirrors this exactly
  // so admins can see what one more click will do.
  const nextLabel =
    fulfilled === 'manual'
      ? 'Rejected'
      : fulfilled === 'rejected'
        ? 'Pending'
        : 'Fulfilled';
  const adminHint = isAdmin ? `\n\nClick to mark as ${nextLabel}` : '';

  const tooltipText = `Group preference: ${resolution.raw}${fragmentSection}${noteSection}\n\nStatus: ${statusLine}${adminHint}`;

  const cycleFulfilled = async () => {
    // 'auto' → 'manual' is a promotion; everything else follows the
    // 3-state cycle defined in the header comment.
    const next: 'manual' | 'rejected' | null =
      fulfilled === undefined
        ? 'manual'
        : fulfilled === 'manual'
          ? 'rejected'
          : fulfilled === 'rejected'
            ? null
            : 'manual'; // 'auto' promotes to 'manual'
    try {
      await setAttendeeGroupPreferenceFulfilled(actor, attendee, next);
    } catch (err) {
      toast.error(
        `Could not update preference status: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      );
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!isAdmin) return;
    e.stopPropagation();
    void cycleFulfilled();
  };

  // Enter / Space activate the icon when focused via keyboard. The Box
  // is `role='button'` (only when interactive) so screen readers
  // announce it correctly.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isAdmin) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      void cycleFulfilled();
    }
  };

  const fullName = `${attendee.first_name} ${attendee.last_name}`.trim();
  const ariaLabel = isAdmin
    ? `Cycle preference status for ${fullName}`
    : `Group preference for ${fullName}`;

  return (
    <Tooltip
      title={tooltipText}
      placement='top'
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap' } } }}
    >
      <Box
        component='span'
        tabIndex={0}
        role={isAdmin ? 'button' : undefined}
        aria-label={ariaLabel}
        onClick={isAdmin ? handleClick : undefined}
        onKeyDown={isAdmin ? handleKeyDown : undefined}
        sx={{
          display: 'inline-flex',
          cursor: isAdmin ? 'pointer' : 'default',
          // Hidden in print: the icon's value lives in its hover tooltip,
          // which doesn't render on paper. Matches the pencil-button
          // sibling in `group-preference-note-editor.tsx`.
          displayPrint: 'none',
        }}
      >
        <Person fontSize='small' color={color} sx={{ fontSize: '1.1rem' }} />
      </Box>
    </Tooltip>
  );
};

export default GroupPreferenceIcon;
